import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Building2, ChevronRight, ChevronLeft, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

type Step = 1 | 2 | 3;
type Plan = 'trial' | 'starter' | 'pro' | 'enterprise';

const STEPS = [
  { n: 1, label: 'Organisation' },
  { n: 2, label: 'Admin User' },
  { n: 3, label: 'Review & Provision' },
];

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const SuperadminProvisioning = () => {
  const { toast } = useToast();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1 fields
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [plan, setPlan] = useState<Plan>('trial');
  const [slugManual, setSlugManual] = useState(false);

  // Step 2 fields
  const [adminEmail, setAdminEmail] = useState('');

  const handleNameChange = (v: string) => {
    setOrgName(v);
    if (!slugManual) setOrgSlug(slugify(v));
  };

  const handleSlugChange = (v: string) => {
    setSlugManual(true);
    setOrgSlug(slugify(v));
  };

  const step1Valid = orgName.trim().length > 0 && orgSlug.length > 0;
  const step2Valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail);

  const provision = async () => {
    setLoading(true);
    setError(null);
    try {
      // 1. Insert tenant
      const { data: tenant, error: tenantErr } = await supabase
        .from('tenants')
        .insert({ name: orgName.trim(), slug: orgSlug, plan })
        .select('id')
        .single();

      if (tenantErr) throw new Error(`Tenant creation failed: ${tenantErr.message}`);

      // 2. Link (or invite) the admin — done server-side via the Admin API
      // since inviting a not-yet-registered user requires the service role.
      if (adminEmail.trim()) {
        const { error: provisionErr } = await supabase.functions.invoke('superadmin-provision-admin', {
          body: { tenantId: tenant.id, email: adminEmail.trim(), role: 'admin' },
        });
        if (provisionErr) throw new Error(`Admin provisioning failed: ${provisionErr.message}`);
      }

      setDone(true);
      toast({ title: `${orgName} provisioned successfully` });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[60vh] gap-6">
        <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-emerald-600" />
        </div>
        <div className="text-center">
          <h2 className="text-2xl font-bold">Organisation Provisioned</h2>
          <p className="text-muted-foreground mt-2">
            <strong>{orgName}</strong> ({orgSlug}) is live on the <strong>{plan}</strong> plan.
          </p>
          {adminEmail && (
            <p className="text-sm text-muted-foreground mt-1">
              Admin: {adminEmail} — they can sign in at <code className="bg-muted px-1 rounded">/{orgSlug}/app</code>
            </p>
          )}
        </div>
        <Button onClick={() => { setDone(false); setStep(1); setOrgName(''); setOrgSlug(''); setAdminEmail(''); setSlugManual(false); setPlan('trial'); }}>
          Provision Another
        </Button>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Building2 className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Provision Organisation</h1>
          <p className="text-muted-foreground text-sm">Create a new tenant and seed the first admin user</p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s.n} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              step > s.n ? 'bg-emerald-500 text-white' :
              step === s.n ? 'bg-primary text-primary-foreground' :
              'bg-muted text-muted-foreground'
            }`}>
              {step > s.n ? <CheckCircle2 className="h-4 w-4" /> : s.n}
            </div>
            <span className={`text-sm ${step === s.n ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
            {i < STEPS.length - 1 && <div className="w-8 h-px bg-border mx-1" />}
          </div>
        ))}
      </div>

      {/* Step content */}
      <Card>
        {step === 1 && (
          <>
            <CardHeader>
              <CardTitle>Organisation Details</CardTitle>
              <CardDescription>Set the name, URL slug, and billing plan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Organisation Name *</Label>
                <Input value={orgName} onChange={e => handleNameChange(e.target.value)} placeholder="Acme Corp" />
              </div>
              <div className="space-y-2">
                <Label>URL Slug *</Label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground shrink-0">masira.net/</span>
                  <Input value={orgSlug} onChange={e => handleSlugChange(e.target.value)} placeholder="acme-corp" className="font-mono" />
                </div>
                <p className="text-xs text-muted-foreground">Only lowercase letters, numbers, and hyphens</p>
              </div>
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={plan} onValueChange={v => setPlan(v as Plan)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </>
        )}

        {step === 2 && (
          <>
            <CardHeader>
              <CardTitle>Admin User</CardTitle>
              <CardDescription>Seed the org's Admin. If this user already exists in Masira they will be granted Admin access immediately.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Admin Email *</Label>
                <Input type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)} placeholder="admin@acme.com" />
              </div>
              <Alert>
                <AlertDescription className="text-sm">
                  If this user hasn't signed up yet, they'll receive an email invite to join <strong>{orgSlug || 'this organisation'}</strong> — clicking it takes them straight to setting a password and into the app.
                </AlertDescription>
              </Alert>
            </CardContent>
          </>
        )}

        {step === 3 && (
          <>
            <CardHeader>
              <CardTitle>Review & Provision</CardTitle>
              <CardDescription>Confirm the details before creating the organisation</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-lg border divide-y divide-border text-sm">
                {[
                  { label: 'Organisation', value: orgName },
                  { label: 'Slug', value: orgSlug },
                  { label: 'Plan', value: plan },
                  { label: 'Admin Email', value: adminEmail || '(none — set later)' },
                ].map(r => (
                  <div key={r.label} className="flex items-center justify-between px-4 py-3">
                    <span className="text-muted-foreground">{r.label}</span>
                    <span className="font-medium">{r.value}</span>
                  </div>
                ))}
              </div>
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </>
        )}
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setStep(s => (s - 1) as Step)}
          disabled={step === 1}
          className="gap-2"
        >
          <ChevronLeft className="h-4 w-4" /> Back
        </Button>

        {step < 3 ? (
          <Button
            onClick={() => setStep(s => (s + 1) as Step)}
            disabled={step === 1 ? !step1Valid : !step2Valid}
            className="gap-2"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={provision} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
            Provision Organisation
          </Button>
        )}
      </div>
    </div>
  );
};

export default SuperadminProvisioning;
